/**
 * App shell. UI_UX section 6.
 *
 * Top bar: logo left, nav center as underlined tabs, right side tCOIN balance,
 * avatar and the settings gear. The content panel below fills the rest and is
 * the only thing that changes between routes; the route change crossfades.
 *
 * Below 900px the nav moves to a bottom tab bar with icons and labels, and the
 * balance moves under the logo, so nothing in the shell needs hover.
 *
 * The battlefield renders inside the same shell but asks for a full-bleed
 * content area, because its own layout owns the whole viewport.
 */

import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useProfile, useSettings, applyDocumentSettings } from '@/app/stores';
import { Logo } from '@/ui/brand/Logo';
import { UiIcon } from '@/ui/icons/UiIcon';
import { SettingsDrawer } from '@/ui/system';
import { AchievementToasts } from '@/achievements';
import { NAV_ITEMS, ROUTES } from './routes';
import styles from './AppShell.module.css';

export function AppShell() {
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const tcoin = useProfile((s) => s.tcoin);
  const username = useProfile((s) => s.username);
  const settings = useSettings();

  // Keep the document attributes in step even before the store rehydrates.
  useEffect(() => {
    applyDocumentSettings(settings);
  }, [settings]);

  // The battlefield owns the viewport; every other route gets the padded panel.
  const immersive = location.pathname.startsWith(ROUTES.playBot) || location.pathname.startsWith(ROUTES.playDeckTest);

  return (
    <div className={styles.shell} data-immersive={immersive}>
      <header className={styles.topbar}>
        <NavLink to={ROUTES.play} className={styles.logo} aria-label="GAS WARS home">
          <Logo size={24} />
        </NavLink>

        <nav className={styles.nav} aria-label="Main">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.key}
              to={item.path}
              className={({ isActive }) => [styles.tab, isActive ? styles.tabActive : ''].filter(Boolean).join(' ')}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className={styles.right}>
          <span className={styles.balance} title="Testnet coins">
            <span className={styles.balanceLabel}>tCOIN</span>
            <span className="num">{tcoin.toLocaleString('en-US')}</span>
          </span>
          <NavLink to={ROUTES.profile} className={styles.avatar} aria-label={`Profile: ${username}`}>
            {username.slice(0, 1).toUpperCase()}
          </NavLink>
          <button
            className={styles.gear}
            onClick={() => setSettingsOpen(true)}
            aria-label="Settings"
            data-testid="open-settings"
          >
            <UiIcon name="gear" size={18} />
          </button>
        </div>
      </header>

      <main className={styles.content} key={location.pathname.split('/').slice(0, 2).join('/')}>
        <Outlet />
      </main>

      <nav className={styles.tabbar} aria-label="Main, compact">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.key}
            to={item.path}
            className={({ isActive }) =>
              [styles.tabbarItem, isActive ? styles.tabbarItemActive : ''].filter(Boolean).join(' ')
            }
          >
            <UiIcon name={item.icon} size={19} />
            <span className={styles.tabbarLabel}>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <AchievementToasts />
    </div>
  );
}
